import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Phone, Sparkles, X } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import {
	useCrmWebSocket,
	type CrmNewLeadEvent,
} from "@/hooks/useCrmWebSocket";

const AUTO_DISMISS_MS = 45_000;
const MAX_VISIBLE = 2;

type AlertItem = CrmNewLeadEvent["lead"] & { showUntil: number };

function leadsListPathForRole(role: string | undefined): string {
	if (role === "HOSPITAL_ADMIN") return "/hospital-admin/leads";
	if (role === "SUPER_ADMIN") return "/super-admin/leads";
	if (role === "SALES_PERSON" || role === "SALES_MANAGER") return "/sales-person/leads";
	return "/sales-person/leads";
}

function formatRelative(iso: string): string {
	const t = Date.parse(iso);
	if (Number.isNaN(t)) return "Just now";
	const sec = Math.max(0, Math.floor((Date.now() - t) / 1000));
	if (sec < 15) return "Just now";
	if (sec < 60) return `${sec}s ago`;
	const min = Math.floor(sec / 60);
	if (min < 60) return `${min}m ago`;
	return new Date(t).toLocaleTimeString("en-IN", {
		hour: "2-digit",
		minute: "2-digit",
	});
}

function playSoftChime() {
	try {
		const Ctx =
			window.AudioContext ||
			(window as unknown as { webkitAudioContext?: typeof AudioContext })
				.webkitAudioContext;
		if (!Ctx) return;
		const ctx = new Ctx();
		const now = ctx.currentTime;
		const gain = ctx.createGain();
		gain.gain.setValueAtTime(0.0001, now);
		gain.gain.exponentialRampToValueAtTime(0.08, now + 0.02);
		gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.45);
		gain.connect(ctx.destination);

		const o1 = ctx.createOscillator();
		o1.type = "sine";
		o1.frequency.setValueAtTime(880, now);
		o1.connect(gain);
		o1.start(now);
		o1.stop(now + 0.2);

		const o2 = ctx.createOscillator();
		o2.type = "sine";
		o2.frequency.setValueAtTime(1174.7, now + 0.12);
		o2.connect(gain);
		o2.start(now + 0.12);
		o2.stop(now + 0.45);

		window.setTimeout(() => {
			void ctx.close().catch(() => undefined);
		}, 600);
	} catch {
		/* autoplay / unsupported */
	}
}

/**
 * Global premium toast stack for NEW_LEAD WebSocket events (sales person + manager).
 */
export default function NewLeadAlertPopup() {
	const { user } = useAuth();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const [alerts, setAlerts] = useState<AlertItem[]>([]);
	const hoveredIds = useRef(new Set<string>());
	const armedIds = useRef(new Set<string>());
	const seenIds = useRef(new Set<string>());

	const enabled =
		!!user?.id &&
		(user.role === "SALES_PERSON" || user.role === "SALES_MANAGER");

	const dismiss = useCallback((id: string) => {
		hoveredIds.current.delete(id);
		armedIds.current.delete(id);
		setAlerts((prev) => prev.filter((x) => x.id !== id));
	}, []);

	const shouldShowLead = useCallback(
		(event: CrmNewLeadEvent) => {
			if (!user?.id) return false;
			const lead = event.lead;
			const recipients = event.recipientStaffIds;
			if (recipients?.length) {
				return recipients.includes(user.id);
			}
			if (user.role === "SALES_PERSON") {
				return lead.assignedTo?.id === user.id;
			}
			if (user.role === "SALES_MANAGER") {
				if (!lead.assignedTo?.id) return true;
				if (lead.assignedTo.id === user.id) return true;
				return lead.assignedTo.managerId === user.id;
			}
			return false;
		},
		[user?.id, user?.role]
	);

	const onNewLead = useCallback(
		(event: CrmNewLeadEvent) => {
			const lead = event.lead;
			if (!lead?.id) return;
			if (!shouldShowLead(event)) return;
			if (seenIds.current.has(lead.id)) return;
			seenIds.current.add(lead.id);
			if (seenIds.current.size > 200) {
				const first = seenIds.current.values().next().value;
				if (first) seenIds.current.delete(first);
			}

			void queryClient.invalidateQueries({ queryKey: ["leads"] });
			playSoftChime();

			const item: AlertItem = {
				...lead,
				showUntil: Date.now() + AUTO_DISMISS_MS,
			};
			setAlerts((prev) => (prev.some((p) => p.id === item.id) ? prev : [...prev, item]));
		},
		[queryClient, shouldShowLead]
	);

	useCrmWebSocket({ enabled, onNewLead });

	const visible = alerts.slice(0, MAX_VISIBLE);

	// Arm auto-dismiss clock when a card first becomes visible (queued → shown).
	useEffect(() => {
		for (const lead of visible) {
			if (armedIds.current.has(lead.id)) continue;
			armedIds.current.add(lead.id);
			setAlerts((prev) =>
				prev.map((a) =>
					a.id === lead.id ? { ...a, showUntil: Date.now() + AUTO_DISMISS_MS } : a
				)
			);
		}
	}, [visible.map((v) => v.id).join(",")]);

	useEffect(() => {
		if (visible.length === 0) return;
		const tick = window.setInterval(() => {
			const now = Date.now();
			for (const lead of visible) {
				if (hoveredIds.current.has(lead.id)) continue;
				if (now >= lead.showUntil) dismiss(lead.id);
			}
		}, 1000);
		return () => window.clearInterval(tick);
	}, [visible, dismiss]);

	if (!enabled || visible.length === 0) return null;

	return (
		<div
			className="pointer-events-none fixed bottom-5 right-5 z-[80] flex w-[min(100vw-1.5rem,22.5rem)] flex-col-reverse gap-3"
			aria-live="polite"
		>
			{visible.map((lead) => (
				<div
					key={lead.id}
					className="pointer-events-auto overflow-hidden rounded-2xl border border-white/20 shadow-2xl shadow-slate-900/25"
					style={{
						background:
							"linear-gradient(145deg, rgba(21,77,146,0.94) 0%, rgba(12,42,84,0.97) 55%, rgba(8,28,58,0.98) 100%)",
						backdropFilter: "blur(16px)",
					}}
					onMouseEnter={() => hoveredIds.current.add(lead.id)}
					onMouseLeave={() => hoveredIds.current.delete(lead.id)}
				>
					<div
						className="h-1 w-full"
						style={{
							background:
								"linear-gradient(90deg, #5eead4, var(--brand-primary, #154D92), #38bdf8)",
						}}
					/>
					<div className="relative p-4 text-white">
						<button
							type="button"
							aria-label="Dismiss"
							className="absolute right-3 top-3 rounded-full p-1 text-white/70 transition hover:bg-white/10 hover:text-white"
							onClick={() => dismiss(lead.id)}
						>
							<X className="h-4 w-4" />
						</button>

						<div className="mb-3 flex items-center gap-2">
							<span className="inline-flex items-center gap-1 rounded-full bg-teal-400/20 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-teal-200 ring-1 ring-teal-300/30">
								<Sparkles className="h-3 w-3" />
								New lead
							</span>
							<span className="text-[11px] text-white/55">
								{formatRelative(lead.createdAt)}
							</span>
						</div>

						<div className="pr-6 text-lg font-semibold leading-snug tracking-tight">
							{lead.patientName || "Unknown"}
						</div>

						{lead.phone ? (
							<div className="mt-1.5 flex items-center gap-1.5 text-sm text-white/80">
								<Phone className="h-3.5 w-3.5 opacity-70" />
								<span className="tabular-nums">{lead.phone}</span>
							</div>
						) : null}

						<div className="mt-3 space-y-1 text-xs text-white/65">
							<div>
								<span className="text-white/45">Source</span>{" "}
								<span className="font-medium text-white/90">
									{lead.source || "—"}
								</span>
							</div>
							{lead.campaignName ? (
								<div className="truncate">
									<span className="text-white/45">Campaign</span>{" "}
									<span className="font-medium text-white/90">
										{lead.campaignName}
									</span>
								</div>
							) : null}
							{lead.assignedTo?.name ? (
								<div>
									<span className="text-white/45">Owner</span>{" "}
									<span className="font-medium text-white/90">
										{lead.assignedTo.name}
									</span>
								</div>
							) : (
								<div>
									<span className="text-white/45">Owner</span>{" "}
									<span className="font-medium text-amber-200">Unassigned</span>
								</div>
							)}
						</div>

						{alerts.length > MAX_VISIBLE ? (
							<div className="mt-2 text-[11px] text-white/50">
								+{alerts.length - MAX_VISIBLE} more waiting
							</div>
						) : null}

						<div className="mt-4 flex items-center gap-2">
							<button
								type="button"
								className="flex-1 rounded-xl bg-white px-3 py-2 text-sm font-semibold text-slate-900 shadow-sm transition hover:bg-teal-50"
								onClick={() => {
									const base = leadsListPathForRole(user?.role);
									dismiss(lead.id);
									navigate(
										`${base}?openLeadId=${encodeURIComponent(lead.id)}`
									);
								}}
							>
								View lead
							</button>
							<button
								type="button"
								className="rounded-xl border border-white/25 px-3 py-2 text-sm font-medium text-white/85 transition hover:bg-white/10"
								onClick={() => dismiss(lead.id)}
							>
								Dismiss
							</button>
						</div>
					</div>
				</div>
			))}
		</div>
	);
}
